import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";

export interface AuditEventDto {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  reason: string | null;
  createdAt: string;
  actor: { id: string; name: string; email: string };
  beforeJson: unknown;
  afterJson: unknown;
}

export interface ClosedPeriodDto {
  id: string;
  month: string;
  closedAt: string;
  closedBy: { id: string; name: string };
  reopenedAt: string | null;
  reopenedBy: { id: string; name: string } | null;
  reopenReason: string | null;
  status: "closed" | "reopened";
}

export interface ReconciliationSummaryDto {
  id: string;
  bankAccountId: string;
  bankAccountName: string;
  statementDate: string;
  statementClosingBalance: string;
  bookBalance: string;
  difference: string;
  status: string;
  preparedBy: { id: string; name: string };
  createdAt: string;
}

export interface ReconciliationItemDto {
  id: string;
  movementType: string;
  movementId: string;
  cleared: boolean;
  outstandingReason: string | null;
  date: string;
  reference: string;
  description: string;
  amount: string;
  direction: "in" | "out";
}

export interface ReconciliationDetailDto extends ReconciliationSummaryDto {
  notes: string | null;
  items: ReconciliationItemDto[];
}

export interface AuditFilters {
  from?: string;
  to?: string;
  action?: string;
  accessChangesOnly?: boolean;
}

export function useAuditEventsQuery(filters: AuditFilters, enabled = true) {
  const params = new URLSearchParams();
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.action) params.set("action", filters.action);
  if (filters.accessChangesOnly) params.set("accessChangesOnly", "true");
  const query = params.toString();

  return useQuery({
    queryKey: ["settings", "audit", filters],
    queryFn: () =>
      api.get<{ events: AuditEventDto[] }>(
        `/audit-events${query ? `?${query}` : ""}`,
      ),
    enabled,
  });
}

export function useClosedPeriodsQuery(enabled = true) {
  return useQuery({
    queryKey: ["settings", "closed-periods"],
    queryFn: () => api.get<{ periods: ClosedPeriodDto[] }>("/closed-periods"),
    enabled,
  });
}

export function useClosePeriodMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (month: string) =>
      api.post<{ period: ClosedPeriodDto }>("/closed-periods/close", { month }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["settings", "closed-periods"] });
    },
  });
}

export function useReopenPeriodMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ month, reason }: { month: string; reason: string }) =>
      api.post<{ period: ClosedPeriodDto }>("/closed-periods/reopen", { month, reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["settings", "closed-periods"] });
    },
  });
}

export function useReconciliationsQuery(enabled = true) {
  return useQuery({
    queryKey: ["settings", "reconciliations"],
    queryFn: () =>
      api.get<{ reconciliations: ReconciliationSummaryDto[] }>("/reconciliations"),
    enabled,
  });
}

export function useReconciliationQuery(id: string | null) {
  return useQuery({
    queryKey: ["settings", "reconciliation", id],
    queryFn: () =>
      api.get<{ reconciliation: ReconciliationDetailDto }>(`/reconciliations/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateReconciliationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      bankAccountId: string;
      statementDate: string;
      statementClosingBalance: string;
      notes?: string;
    }) => api.post<{ reconciliation: ReconciliationDetailDto }>("/reconciliations", body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["settings", "reconciliations"] });
    },
  });
}

export function useUpdateReconciliationItemsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      items,
    }: {
      id: string;
      items: { id: string; cleared: boolean; outstandingReason?: string }[];
    }) =>
      api.patch<{ reconciliation: ReconciliationDetailDto }>(
        `/reconciliations/${id}/items`,
        { items },
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["settings", "reconciliations"] });
      void queryClient.invalidateQueries({
        queryKey: ["settings", "reconciliation", variables.id],
      });
    },
  });
}

export function useFinalizeReconciliationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ reconciliation: ReconciliationDetailDto }>(
        `/reconciliations/${id}/finalize`,
      ),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: ["settings", "reconciliations"] });
      void queryClient.invalidateQueries({ queryKey: ["settings", "reconciliation", id] });
    },
  });
}

export interface UserUnitScopeDto {
  unitId: string;
  unitCode: string;
  unitName: string;
  canRecordPayments: boolean;
}

export interface UserAdminDto {
  id: string;
  name: string;
  email: string;
  role: string;
  donorId: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  unitScopes: UserUnitScopeDto[];
}

export interface UserUnitScopeInput {
  unitId: string;
  canRecordPayments?: boolean;
}

export function useUsersQuery(enabled = true) {
  return useQuery({
    queryKey: ["settings", "users"],
    queryFn: () => api.get<{ users: UserAdminDto[] }>("/settings/users"),
    enabled,
  });
}

export function useUserQuery(id: string | null) {
  return useQuery({
    queryKey: ["settings", "users", id],
    queryFn: () => api.get<{ user: UserAdminDto }>(`/settings/users/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateUserMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      email: string;
      name: string;
      role: string;
      password: string;
      donorId?: string | null;
      unitScopes?: UserUnitScopeInput[];
    }) => api.post<{ user: UserAdminDto }>("/settings/users", body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["settings", "users"] });
    },
  });
}

export function useUpdateUserMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      role?: string;
      active?: boolean;
      password?: string;
      donorId?: string | null;
      unitScopes?: UserUnitScopeInput[];
    }) => api.patch<{ user: UserAdminDto }>(`/settings/users/${id}`, body),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["settings", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["settings", "users", variables.id] });
    },
  });
}

export interface CategoryAdminDto {
  id: string;
  name: string;
  type: "income" | "expense";
  active: boolean;
}

export interface BankAccountAdminDto {
  id: string;
  name: string;
  kind: "bank" | "cash";
  active: boolean;
}

export interface UnitAdminDto {
  id: string;
  name: string;
  code: string;
  groupId: string;
  groupName: string;
  groupCode: string;
  fiscalYearId: string;
  fiscalYearName: string;
  annualBudget: string;
  active: boolean;
}

export interface OpeningBalanceAdminDto {
  id: string;
  bankAccountId: string;
  bankAccountName: string;
  fundId: string;
  fundName: string;
  unitId: string;
  unitCode: string;
  unitName: string;
  amount: string;
  effectiveDate: string;
}

export function useSettingsCategoriesQuery(enabled = true) {
  return useQuery({
    queryKey: ["settings", "categories"],
    queryFn: () => api.get<{ categories: CategoryAdminDto[] }>("/settings/categories"),
    enabled,
  });
}

export function useCreateSettingsCategoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; type: "income" | "expense" }) =>
      api.post<{ category: CategoryAdminDto }>("/settings/categories", body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["settings", "categories"] });
      void queryClient.invalidateQueries({ queryKey: ["reference", "categories"] });
    },
  });
}

export function useSettingsBankAccountsQuery(enabled = true) {
  return useQuery({
    queryKey: ["settings", "bank-accounts"],
    queryFn: () =>
      api.get<{ bankAccounts: BankAccountAdminDto[] }>("/settings/bank-accounts"),
    enabled,
  });
}

export function useCreateSettingsBankAccountMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; kind: "bank" | "cash" }) =>
      api.post<{ bankAccount: BankAccountAdminDto }>("/settings/bank-accounts", body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["settings", "bank-accounts"] });
      void queryClient.invalidateQueries({ queryKey: ["reference", "bank-accounts"] });
    },
  });
}

export function useSettingsUnitsQuery(enabled = true) {
  return useQuery({
    queryKey: ["settings", "units"],
    queryFn: () => api.get<{ units: UnitAdminDto[] }>("/settings/units"),
    enabled,
  });
}

export function useCreateSettingsUnitMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      groupId: string;
      name: string;
      code: string;
      annualBudget: string;
    }) => api.post<{ unit: UnitAdminDto }>("/settings/units", body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["settings", "units"] });
      void queryClient.invalidateQueries({ queryKey: ["overview", "programmes"] });
    },
  });
}

export function useUpdateSettingsUnitMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      annualBudget?: string;
      active?: boolean;
    }) => api.patch<{ unit: UnitAdminDto }>(`/settings/units/${id}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["settings", "units"] });
      void queryClient.invalidateQueries({ queryKey: ["overview"] });
    },
  });
}

export function useSettingsOpeningBalancesQuery(enabled = true) {
  return useQuery({
    queryKey: ["settings", "opening-balances"],
    queryFn: () =>
      api.get<{ openingBalances: OpeningBalanceAdminDto[] }>("/settings/opening-balances"),
    enabled,
  });
}

export function useCreateSettingsOpeningBalanceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      bankAccountId: string;
      fundId: string;
      unitId: string;
      amount: string;
      effectiveDate: string;
    }) =>
      api.post<{ openingBalance: OpeningBalanceAdminDto }>(
        "/settings/opening-balances",
        body,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["settings", "opening-balances"] });
      void queryClient.invalidateQueries({ queryKey: ["overview"] });
    },
  });
}
